import { useCallback, useEffect, useRef, useState } from 'react';
import type { UpOrDown } from '@hygh-vybe/lib';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';

export interface RatingData {
  ratingValue: number;
  buzzScore: number;
  upOrDown: UpOrDown;
}

export interface ReviewData {
  userId: string;
  displayName: string;
  productId: string;
  reviewText: string;
  ratingValue?: number;
  buzzScore?: number;
  upOrDown?: UpOrDown;
  location?: string;
  isFirstReview?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RatingAndReviewSubmission {
  ratingValue: number;
  buzzScore: number;
  upOrDown: UpOrDown;
  reviewText?: string;
  location?: string;
  isFirstReview?: boolean;
}

export interface UseRatingsAndReviewsResult {
  // Rating state
  existingRating: RatingData | null;
  ratingLoading: boolean;
  ratingSubmitting: boolean;
  submitRating: (values: RatingData) => Promise<void>;

  // Review state
  reviews: ReviewData[];
  reviewsLoading: boolean;
  hasMoreReviews: boolean;
  loadMoreReviews: () => void;
  existingReview: ReviewData | null;
  reviewSubmitting: boolean;
  submitReview: (text: string) => Promise<void>;
  editReview: (text: string) => Promise<void>;
  deleteReview: () => Promise<void>;
  deleteSubmitting: boolean;

  // Combined submission
  submitRatingAndReview: (values: RatingAndReviewSubmission) => Promise<void>;
  combinedSubmitting: boolean;
}

export function useRatingsAndReviews(
  productId: string,
  token: string | null,
  userId: string | null
): UseRatingsAndReviewsResult {
  const { authFetch, user } = useAuth();
  const [existingRating, setExistingRating] = useState<RatingData | null>(null);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  const [reviews, setReviews] = useState<ReviewData[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [hasMoreReviews, setHasMoreReviews] = useState(false);
  const [reviewCursor, setReviewCursor] = useState<string | null>(null);
  const [existingReview, setExistingReview] = useState<ReviewData | null>(null);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [combinedSubmitting, setCombinedSubmitting] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;

  // Fetch user's existing rating for this product
  useEffect(() => {
    if (!token || !userId || !productId) return;

    setRatingLoading(true);
    const controller = new AbortController();

    authFetchRef.current(`${API_BASE}/v1/ratings/${productId}`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (res.status === 404) return null;
        if (!res.ok) throw new Error('Failed to fetch rating');
        return res.json();
      })
      .then((json) => {
        if (json?.data) {
          setExistingRating({
            ratingValue: json.data.ratingValue,
            buzzScore: json.data.buzzScore,
            upOrDown: json.data.upOrDown,
          });
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          // Silently ignore — user may not have rated yet
        }
      })
      .finally(() => setRatingLoading(false));

    return () => controller.abort();
  }, [productId, token, userId]);

  // Fetch reviews for this product (public endpoint — no auth needed)
  useEffect(() => {
    if (!productId) return;
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    setReviewsLoading(true);

    fetch(`${API_BASE}/v1/products/${productId}/reviews?limit=20`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch reviews');
        return res.json();
      })
      .then((json) => {
        const data: ReviewData[] = json.data ?? [];
        setReviews(data);
        setReviewCursor(json.metadata?.pagination?.nextCursor ?? null);
        setHasMoreReviews(!!json.metadata?.pagination?.nextCursor);

        // Find the user's own review
        if (userId) {
          const own = data.find((r) => r.userId === userId) ?? null;
          setExistingReview(own);
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setReviews([]);
        }
      })
      .finally(() => setReviewsLoading(false));

    return () => controller.abort();
  }, [productId, userId]);

  const loadMoreReviews = useCallback(async () => {
    if (!reviewCursor || reviewsLoading) return;

    setReviewsLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/v1/products/${productId}/reviews?limit=20&cursor=${reviewCursor}`
      );
      if (!res.ok) throw new Error('Failed to load more reviews');
      const json = await res.json();
      const data: ReviewData[] = json.data ?? [];
      setReviews((prev) => [...prev, ...data]);
      setReviewCursor(json.metadata?.pagination?.nextCursor ?? null);
      setHasMoreReviews(!!json.metadata?.pagination?.nextCursor);
    } catch {
      // Silently handle
    } finally {
      setReviewsLoading(false);
    }
  }, [productId, reviewCursor, reviewsLoading]);

  const submitRating = useCallback(
    async (values: RatingData) => {
      if (!token) throw new Error('Not authenticated');
      setRatingSubmitting(true);
      try {
        const isUpdate = !!existingRating;
        const method = isUpdate ? 'PUT' : 'POST';
        const url = isUpdate
          ? `${API_BASE}/v1/ratings/${productId}`
          : `${API_BASE}/v1/ratings`;

        const body = isUpdate
          ? { ratingValue: values.ratingValue, buzzScore: values.buzzScore, upOrDown: values.upOrDown }
          : { productId, ratingValue: values.ratingValue, buzzScore: values.buzzScore, upOrDown: values.upOrDown };

        const res = await authFetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        // Handle 409 Conflict: user already has a rating — retry as PUT (edit mode)
        if (res.status === 409 && !isUpdate) {
          const retryRes = await authFetch(`${API_BASE}/v1/ratings/${productId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ratingValue: values.ratingValue,
              buzzScore: values.buzzScore,
              upOrDown: values.upOrDown,
            }),
          });

          if (!retryRes.ok) {
            const json = await retryRes.json().catch(() => null);
            throw new Error(json?.error?.message || 'Failed to update rating');
          }

          setExistingRating(values);
          return;
        }

        if (!res.ok) {
          const json = await res.json().catch(() => null);
          throw new Error(json?.error?.message || 'Failed to submit rating');
        }

        setExistingRating(values);
      } finally {
        setRatingSubmitting(false);
      }
    },
    [token, authFetch, existingRating, productId]
  );

  const submitReview = useCallback(
    async (text: string) => {
      if (!token) throw new Error('Not authenticated');
      setReviewSubmitting(true);
      try {
        const res = await authFetch(`${API_BASE}/v1/reviews`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId, reviewText: text, displayName: user?.displayName || user?.email?.split('@')[0] || 'User' }),
        });

        // Handle 409 Conflict: user already has a review for this product
        if (res.status === 409) {
          throw new Error('You have already reviewed this product. Please edit your existing review instead.');
        }

        if (!res.ok) {
          const json = await res.json().catch(() => null);
          throw new Error(json?.error?.message || 'Failed to submit review');
        }

        const json = await res.json();
        const newReview: ReviewData = json.data;
        setReviews((prev) => [newReview, ...prev]);
        setExistingReview(newReview);
      } finally {
        setReviewSubmitting(false);
      }
    },
    [token, authFetch, productId, user?.displayName]
  );

  const editReview = useCallback(
    async (text: string) => {
      if (!token) throw new Error('Not authenticated');
      setReviewSubmitting(true);
      try {
        const res = await authFetch(`${API_BASE}/v1/reviews/${productId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviewText: text }),
        });

        if (!res.ok) {
          const json = await res.json().catch(() => null);
          throw new Error(json?.error?.message || 'Failed to update review');
        }

        const json = await res.json();
        const updated: ReviewData = json.data;
        setReviews((prev) =>
          prev.map((r) => (r.userId === userId ? updated : r))
        );
        setExistingReview(updated);
      } finally {
        setReviewSubmitting(false);
      }
    },
    [token, authFetch, productId, userId]
  );

  const deleteReview = useCallback(async () => {
    if (!token) throw new Error('Not authenticated');
    setDeleteSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/v1/reviews/${productId}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error?.message || 'Failed to delete review');
      }

      setReviews((prev) => prev.filter((r) => r.userId !== userId));
      setExistingReview(null);
    } finally {
      setDeleteSubmitting(false);
    }
  }, [token, authFetch, productId, userId]);

  /**
   * Combined submission: submits rating first, then review text if provided.
   * After success, adds the review to the displayed list immediately.
   */
  const submitRatingAndReview = useCallback(
    async (values: RatingAndReviewSubmission) => {
      if (!token) throw new Error('Not authenticated');
      setCombinedSubmitting(true);
      try {
        // 1. Submit rating
        const isUpdate = !!existingRating;
        const ratingMethod = isUpdate ? 'PUT' : 'POST';
        const ratingUrl = isUpdate
          ? `${API_BASE}/v1/ratings/${productId}`
          : `${API_BASE}/v1/ratings`;

        const ratingBody = isUpdate
          ? { ratingValue: values.ratingValue, buzzScore: values.buzzScore, upOrDown: values.upOrDown }
          : { productId, ratingValue: values.ratingValue, buzzScore: values.buzzScore, upOrDown: values.upOrDown, ...(values.isFirstReview ? { isFirstReview: true } : {}) };

        const ratingRes = await authFetch(ratingUrl, {
          method: ratingMethod,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ratingBody),
        });

        // Handle 409 Conflict on rating
        if (ratingRes.status === 409 && !isUpdate) {
          const retryRes = await authFetch(`${API_BASE}/v1/ratings/${productId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ratingValue: values.ratingValue,
              buzzScore: values.buzzScore,
              upOrDown: values.upOrDown,
            }),
          });
          if (!retryRes.ok) {
            const json = await retryRes.json().catch(() => null);
            throw new Error(json?.error?.message || 'Failed to update rating');
          }
        } else if (!ratingRes.ok) {
          const json = await ratingRes.json().catch(() => null);
          throw new Error(json?.error?.message || 'Failed to submit rating');
        }

        setExistingRating({
          ratingValue: values.ratingValue,
          buzzScore: values.buzzScore,
          upOrDown: values.upOrDown,
        });

        // 2. Submit review text if provided
        if (values.reviewText && values.reviewText.trim().length > 0) {
          const displayName = user?.displayName || user?.email?.split('@')[0] || 'User';
          const reviewBody: Record<string, unknown> = {
            productId,
            reviewText: values.reviewText.trim(),
            displayName,
            ...(values.isFirstReview ? { isFirstReview: true } : {}),
          };
          if (values.location) {
            reviewBody.location = values.location;
          }

          const isReviewUpdate = !!existingReview;

          if (isReviewUpdate) {
            const res = await authFetch(`${API_BASE}/v1/reviews/${productId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ reviewText: values.reviewText.trim() }),
            });
            if (!res.ok) {
              const json = await res.json().catch(() => null);
              throw new Error(json?.error?.message || 'Failed to update review');
            }
            const json = await res.json();
            const updated: ReviewData = json.data;
            setReviews((prev) =>
              prev.map((r) => (r.userId === userId ? updated : r))
            );
            setExistingReview(updated);
          } else {
            const res = await authFetch(`${API_BASE}/v1/reviews`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(reviewBody),
            });

            if (res.status === 409) {
              // Already reviewed — not a fatal error in combined flow
            } else if (!res.ok) {
              const json = await res.json().catch(() => null);
              throw new Error(json?.error?.message || 'Failed to submit review');
            } else {
              const json = await res.json();
              const newReview: ReviewData = {
                ...json.data,
                ratingValue: values.ratingValue,
                buzzScore: values.buzzScore,
                upOrDown: values.upOrDown,
                location: values.location,
              };
              setReviews((prev) => [newReview, ...prev]);
              setExistingReview(newReview);
            }
          }
        } else {
          // No review text — still show the rating in the review list as a "rating-only" entry
          const syntheticReview: ReviewData = {
            userId: userId || '',
            displayName: user?.displayName || 'You',
            productId,
            reviewText: '',
            ratingValue: values.ratingValue,
            buzzScore: values.buzzScore,
            upOrDown: values.upOrDown,
            location: values.location,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          // Only add if no existing review from this user
          if (!existingReview) {
            setReviews((prev) => {
              const filtered = prev.filter((r) => r.userId !== userId);
              return [syntheticReview, ...filtered];
            });
          }
        }
      } finally {
        setCombinedSubmitting(false);
      }
    },
    [token, authFetch, existingRating, existingReview, productId, userId, user?.displayName]
  );

  return {
    existingRating,
    ratingLoading,
    ratingSubmitting,
    submitRating,
    reviews,
    reviewsLoading,
    hasMoreReviews,
    loadMoreReviews,
    existingReview,
    reviewSubmitting,
    submitReview,
    editReview,
    deleteReview,
    deleteSubmitting,
    submitRatingAndReview,
    combinedSubmitting,
  };
}
